import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from '../toast';
import { useTranslation } from 'react-i18next';
import { getUserNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead, deleteNotification } from '../../reducer/notifications/notificationsReducer';
import { markAsReadLocally } from '../../slice/notifications/notificationsSlice';
import NotificationsPanelView from './NotificationsPanelView';

// A notification is unread until the user's read state is set. Read state is
// tracked by the backend via `isRead`/`readAt` (camelCase in the API response),
// INDEPENDENT of the delivery `status` — so we must not rely on `status` here.
// Freshly-arrived socket notifications have no read flag yet, so they read unread.
const isNotificationUnread = (n) =>
  !(n.isRead || n.is_read || n.readAt || n.read_at || n.status === 'read');

const getNotifId = (notification) =>
  notification.id || notification.notification_id || notification.recipient;

const NotificationsPanel = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // The physio slice keeps `loading` as a single boolean and has no error state.
  const { notifications, pagination, unreadCount, loading } = useSelector(state => state.notifications);

  // Fetch on open
  useEffect(() => {
    if (isOpen) {
      dispatch(getUserNotifications({ page: 1, limit: 20 }));
      dispatch(getUnreadNotificationCount());
    }
  }, [isOpen, dispatch]);

  // Load more
  const loadMore = () => {
    if (!loading && pagination.page < pagination.totalPages) {
      dispatch(getUserNotifications({ page: pagination.page + 1, limit: 20 }));
    }
  };

  const handleMarkAsRead = (notificationId) => {
    dispatch(markAsReadLocally(notificationId));
    dispatch(markNotificationRead({ notificationId }));
  };

  const handleDelete = (notificationId) => {
    dispatch(deleteNotification({ notificationId }));
  };

  const handleMarkAllAsRead = async () => {
    try {
      await dispatch(markAllNotificationsRead()).unwrap();
      dispatch(getUserNotifications({ page: 1, limit: 20 }));
      dispatch(getUnreadNotificationCount());
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleOpen = (notification) => {
    const bookingId = notification.data?.booking_id || notification.data?.order_id;
    handleMarkAsRead(getNotifId(notification));
    onClose();
    if (notification.notification_type === 'NEW_PHYSIO_BOOKING' || notification.notification_type === 'PHYSIO_PAYMENT_RECEIVED' || notification.notification_type === 'PHYSIO_COD_CONFIRMED') {
      navigate('/physio_ordermanagement');
    } else if (bookingId) {
      navigate(`/physio_appointment_detail?id=${bookingId}`);
    }
  };

  // Get localized content
  const getLocalizedContent = (notification) => {
    const isArabic = i18n.language === 'ar';
    const titleArabic = notification.title_arabic || notification.titleArabic;
    const bodyArabic = notification.body_arabic || notification.bodyArabic;
    return {
      title: isArabic && titleArabic ? titleArabic : notification.title || t('notifications.fallbackTitle'),
      body: isArabic && bodyArabic ? bodyArabic : notification.body || '',
    };
  };

  const isChatMessage = (notification) =>
    notification.notification_type === 'CHAT_MESSAGE' ||
    notification.notificationType === 'CHAT_MESSAGE' ||
    notification.title?.toLowerCase() === 'new message';

  if (!isOpen) return null;

  return (
    <NotificationsPanelView
      onClose={onClose}
      labels={{
        title: t('notifications.title'),
        unreadCount: (count) => t('notifications.unreadCount', { count }),
        markAllAsRead: t('notifications.markAllAsRead'),
        loading: t('notifications.loading'),
        failedToLoad: t('notifications.failedToLoad'),
        retry: t('notifications.retry'),
        empty: t('notifications.noNotifications'),
        emptyHint: t('notifications.noNotificationsHint'),
        delete: t('notifications.deleteNotification'),
        loadMore: t('notifications.loadMore'),
        loadingMore: t('notifications.loadingMore'),
      }}
      notifications={notifications}
      unreadCount={unreadCount}
      loading={Boolean(loading)}
      hasMore={pagination.page < pagination.totalPages}
      onLoadMore={loadMore}
      onMarkAllAsRead={handleMarkAllAsRead}
      getKey={getNotifId}
      getTitle={(n) => getLocalizedContent(n).title}
      getMessage={(n) =>
        isChatMessage(n) ? t('notifications.newMessageFromAdmin') : getLocalizedContent(n).body
      }
      isUnread={isNotificationUnread}
      onOpen={handleOpen}
      onDelete={(n) => handleDelete(getNotifId(n))}
    />
  );
};

export default NotificationsPanel;
